import { AfterViewInit, Component, ElementRef, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { DetailsPage as StepDetailsPage } from 'src/app/steps/details/details.page';
import Dropzone from 'dropzone';

@Component({
  selector: 'app-file-dropbox',
  templateUrl: './file-dropbox.page.html',
  styleUrls: ['./file-dropbox.page.scss'],
})
export class FileDropboxPage implements OnInit, AfterViewInit {
  @ViewChild('dropzoneRef', { static: false }) dropzoneElement!: ElementRef;

  subject: string = '';
  user: any = null;

  companies: any[] = [];
  //Either the literal string 'myself' or a selected company object from `companies`
  selected_target: any = 'myself';

  // Set when this modal is opened for a specific email that was itself sent to a company/role (see
  // public/email/email.page.ts's "use the file dropbox" link and steps.page.ts) - lets us preselect
  // "Upload Files As" and the permission level instead of defaulting to "Myself".
  @Input() preselected_company_guid: string | null = null;
  @Input() preselected_role_guid: string | null = null;

  // Roles are firm-wide, not company-specific, so this is fetched once (ngOnInit) and reused for every
  // company the user picks - only their own roles for that company need re-fetching per switch.
  firm_roles: any[] = [];
  // Roles available to pick from for the selected company - theirs or a less-privileged one (1 is the
  // most privileged level, so this is every firm role whose level is >= the best level they hold there).
  available_roles: any[] | null = null;
  selected_role_guid: string | null = null;

  // Any ongoing project the client is assigned to (directly or via a company) - selecting one uploads
  // straight into that project's own "Uploaded Files" folder instead of the general dropbox, bypassing
  // Subject/Upload Files As/Permission Level entirely.
  available_projects: any[] = [];
  selected_project: any = null;
  loading_projects = false;

  // Fetched quietly (not shown as its own picker) just to power the "this project has an active
  // document request" nudge below - each is { step, email }, matched against selected_project via
  // step.project_guid (already selected in ClientSide/Steps.cs).
  private open_document_request_tasks: any[] = [];

  dropzone?: Dropzone;
  pendingFilesCount = 0;
  uploading = false;

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    public globalService: GlobalService,
    private ngZone: NgZone
  ) { }

  async ngOnInit() {
    this.GetUser();
    this.GetAvailableProjects();
    this.GetOpenDocumentRequestTasks();
    await Promise.all([this.GetCompanies(), this.GetFirmRoles()]);
    await this.ApplyPreselection();
  }

  // Wide static range since this modal has no date-filter UI of its own (unlike the Tasks page) -
  // this just needs to know whether an open document-request task exists for the selected project.
  async GetOpenDocumentRequestTasks() {
    const start = new Date();
    start.setFullYear(start.getFullYear() - 2);
    const end = new Date();
    end.setFullYear(end.getFullYear() + 2);

    const data: any = await this.apiService.get(`steps?start_date=${start.toISOString()}&end_date=${end.toISOString()}`);
    if (!data?.success) return;

    this.open_document_request_tasks = (data.data || [])
      .filter((step: any) => step.emails?.[0]?.type === 2 && step.emails[0]?.status !== 3)
      .map((step: any) => ({ step, email: step.emails[0] }));
  }

  // If the selected project has its own open document-request task, uploading there instead is
  // usually what the client actually wants (it counts toward fulfilling that request) - this surfaces
  // that as a nudge with a link that opens the task's own "Complete Task" modal right here, rather
  // than silently uploading to the project's generic "Uploaded Files" folder.
  MatchingTaskForSelectedProject(): any {
    if (!this.selected_project) return null;
    return this.open_document_request_tasks.find((t: any) => t.step.project_guid === this.selected_project.guid) || null;
  }

  // Opens the same fullscreen "Complete Task" modal used on the Tasks page (steps.page.ts,
  // table.component.ts) directly on top of this one - the step object already has the emails array
  // that modal needs (step.emails[0].guid), since it comes from the same client_side/steps endpoint.
  async OpenMatchingTask() {
    const match = this.MatchingTaskForSelectedProject();
    if (!match) return;

    const modal = await this.modalController.create({
      component: StepDetailsPage,
      cssClass: 'fullscreen-modal',
      backdropDismiss: false,
      componentProps: { step: match.step }
    });
    modal.onDidDismiss()
      .then((data) => {
        this.Dismiss();
      });
    await modal.present();
  }

  async GetAvailableProjects() {
    this.loading_projects = true;
    const data: any = await this.apiService.get('projects');
    // project_start_date is a plain date, but comes back with a "T00:00:00" time part attached
    // (e.g. "2026-08-27T00:00:00") - not a real datetime, so DateToUTCFormat doesn't apply; just
    // drop everything from the T onward.
    this.available_projects = data?.success
      ? (data.data || []).map((p: any) => ({
          ...p,
          project_start_date: p.project_start_date ? p.project_start_date.split('T')[0] : p.project_start_date
        }))
      : [];
    this.loading_projects = false;
  }

  // A project is linked to either a company or a single client, never both - a client can only ever
  // see their own client-linked projects (access is scoped by client_id/company in the query itself),
  // so the "person" case is always just the current user.
  GetProjectLinkedName(project: any): string {
    if (project.company_guid) {
      const company = this.companies.find((c: any) => c.guid === project.company_guid);
      return company ? company.name : '';
    }
    if (project.client_guid) {
      return this.user?.display_name || '';
    }
    return '';
  }

  // Preselects "Upload Files As" (and its permission level) when this modal was opened for a
  // specific company/role - see preselected_company_guid/preselected_role_guid above. Requires
  // companies and firm_roles to already be loaded, since OnTargetChange() reads both.
  private async ApplyPreselection() {
    if (!this.preselected_company_guid) return;

    const company = this.companies.find((c: any) => c.guid === this.preselected_company_guid);
    if (!company) return;

    this.selected_target = company;
    await this.OnTargetChange();

    if (this.preselected_role_guid && this.available_roles?.some((r: any) => r.guid === this.preselected_role_guid)) {
      this.selected_role_guid = this.preselected_role_guid;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (!this.dropzoneElement?.nativeElement) return;
      this.dropzone = new Dropzone(this.dropzoneElement.nativeElement, {
        url: '/fake-upload-endpoint',
        autoProcessQueue: false,
        addRemoveLinks: true,
        parallelUploads: 1,
        dictDefaultMessage: 'Drag or click here to upload files',
      });
      this.dropzone.on('addedfile', () => this.updateFileCount());
      this.dropzone.on('removedfile', () => this.updateFileCount());
    }, 300);
  }

  updateFileCount() {
    this.ngZone.run(() => {
      this.pendingFilesCount = this.dropzone?.files?.length ?? 0;
    });
  }

  async GetUser() {
    this.user = await this.globalService.GetUser();
  }

  async GetCompanies() {
    const return_data: any = await this.apiService.get('companies');
    if (return_data?.success) {
      this.companies = return_data.data;
    }
  }

  async GetFirmRoles() {
    const return_data: any = await this.apiService.get('firms/clients/roles');
    if (return_data?.success) {
      this.firm_roles = return_data.data;
    }
  }

  // Called when the "Upload For" selection changes - only companies need a permission level. The full
  // firm role list is already cached (firm_roles) - only their own roles for this company need fetching.
  async OnTargetChange() {
    this.selected_role_guid = null;
    this.available_roles = null;
    if (this.selected_target === 'myself') return;

    const own_roles_data: any = await this.apiService.get(`companies/${this.selected_target.guid}/roles`);
    const own_roles = own_roles_data?.success ? own_roles_data.data : [];
    if (own_roles.length === 0 || this.firm_roles.length === 0) {
      this.available_roles = [];
      return;
    }

    // 1 is the most privileged level - they may only drop files in at their own level or a
    // less-privileged (equal or higher numbered) one, never anything more privileged than they hold.
    const own_min_level = Math.min(...own_roles.map((r: any) => r.level));
    this.available_roles = this.firm_roles
      .filter((r: any) => r.level >= own_min_level)
      .sort((a: any, b: any) => a.level - b.level);
  }

  CanUpload() {
    if (this.uploading) return false;
    if (this.selected_project) return this.pendingFilesCount > 0;
    return !!this.subject && this.pendingFilesCount > 0;
  }

  // Access to a role-restricted folder is granted to a viewer whose own level is <= the folder's
  // required level (1 is most privileged) - i.e. the selected role itself plus everyone MORE senior.
  // Anyone less senior (a higher level number) than the selected role is denied. Mirrors the backend's
  // CanUploadToFolder check in ClientSide/Files.cs exactly, so this reflects real enforced access.
  get RolesWithAccess(): any[] {
    const level = this.SelectedRoleLevel;
    if (level == null) return [];
    return this.firm_roles.filter((r: any) => r.level <= level).sort((a: any, b: any) => a.level - b.level);
  }

  get RolesWithoutAccess(): any[] {
    const level = this.SelectedRoleLevel;
    if (level == null) return [];
    return this.firm_roles.filter((r: any) => r.level > level).sort((a: any, b: any) => a.level - b.level);
  }

  private get SelectedRoleLevel(): number | null {
    if (!this.selected_role_guid) return null;
    const role = this.firm_roles.find((r: any) => r.guid === this.selected_role_guid);
    return role ? role.level : null;
  }

  async Upload() {
    if (!this.dropzone || !this.CanUpload()) return;

    // Snapshot before the loop - removeFile() mutates Dropzone's internal list
    const files = [...this.dropzone.getAcceptedFiles()];
    if (files.length === 0) return;

    this.uploading = true;

    let folder_guid = '';

    if (this.selected_project) {
      // Uploading straight into the project's own "Uploaded Files" folder - that folder already
      // carries project_guid/linked_to_project, so no extra data is needed on the upload itself.
      const folder_data: any = await this.apiService.post('files/project_upload_folder', { project_guid: this.selected_project.guid });
      if (!folder_data?.success) {
        this.globalService.presentToastMessage(folder_data?.message ?? 'Could not prepare the project folder', false);
        this.uploading = false;
        return;
      }
      folder_guid = folder_data.data[0].guid;
    } else {
      const payload: any = { subject: this.subject };
      if (this.selected_target !== 'myself') {
        payload.company_guid = this.selected_target.guid;
        payload.role_guid = this.selected_role_guid;
      }

      const folder_data: any = await this.apiService.post('files/file_dropbox_folder', payload);
      if (!folder_data?.success) {
        this.globalService.presentToastMessage(folder_data?.message ?? 'Could not prepare the upload folder', false);
        this.uploading = false;
        return;
      }
      folder_guid = folder_data.data[0].guid;
    }

    let all_ok = true;
    for (const file of files) {
      const file_guid = await this.apiService.UploadFile(file, folder_guid);
      if (file_guid) {
        this.dropzone.emit('success', file);
        this.dropzone.removeFile(file);
      } else {
        all_ok = false;
        this.dropzone.emit('error', file, 'Upload failed');
      }
    }

    this.uploading = false;
    this.globalService.presentToastMessage(all_ok ? 'Files uploaded' : 'Some files failed to upload', all_ok);

    if (all_ok) this.modalController.dismiss({ uploaded: true });
  }

  async Dismiss() {
    this.modalController.dismiss();
  }

}
